import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Video, ArrowLeft, Save, BookOpen, Calendar, Link, Clock } from 'lucide-react';
import { toast } from 'sonner';

const ScheduleLivePage = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [trainers, setTrainers] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    course_id: '',
    trainer_id: '',
    title: '',
    description: '',
    scheduled_date: '',
    start_time: '',
    end_time: '',
    duration_minutes: 60,
    live_url: '',
    replay_url: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, trainersRes] = await Promise.all([
        api.get('/courses'),
        api.get('/trainers').catch(() => ({ data: [] }))
      ]);
      setCourses(coursesRes.data);
      setTrainers(trainersRes.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-calculate duration when times change
    if (field === 'start_time' || field === 'end_time') {
      const newData = { ...formData, [field]: value };
      if (newData.start_time && newData.end_time) {
        const start = newData.start_time.split(':').map(Number);
        const end = newData.end_time.split(':').map(Number);
        const startMinutes = start[0] * 60 + start[1];
        const endMinutes = end[0] * 60 + end[1];
        if (endMinutes > startMinutes) {
          setFormData(prev => ({ ...prev, [field]: value, duration_minutes: endMinutes - startMinutes }));
          return;
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.course_id || !formData.title || !formData.scheduled_date || !formData.start_time) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      // Combine date and time
      const scheduledAt = `${formData.scheduled_date}T${formData.start_time}:00`;

      await api.post('/live-sessions', {
        course_id: parseInt(formData.course_id),
        trainer_id: formData.trainer_id ? parseInt(formData.trainer_id) : null,
        title: formData.title,
        description: formData.description,
        scheduled_at: scheduledAt,
        duration_minutes: formData.duration_minutes,
        live_url: formData.live_url || null
      });

      toast.success('Session live planifiée ! Le formateur et les étudiants ont été notifiés.');
      navigate('/trainer/planning');
    } catch (error) {
      console.error('Error scheduling live:', error);
      toast.error('Erreur lors de la planification de la session');
    } finally {
      setLoading(false);
    }
  };

  // Get tomorrow's date as minimum
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Planifier une Session Live" onMenuClick={onMenuClick} />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate('/dashboard')}
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour au tableau de bord
        </Button>

        <form onSubmit={handleSubmit}>
          {/* Course & Trainer Selection */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-emerald-600" />
                Cours et Formateur
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Cours *</Label>
                  <Select value={formData.course_id} onValueChange={(v) => handleInputChange('course_id', v)}>
                    <SelectTrigger data-testid="select-course">
                      <SelectValue placeholder="Sélectionner un cours" />
                    </SelectTrigger>
                    <SelectContent>
                      {courses.map(course => (
                        <SelectItem key={course.id} value={course.id.toString()}>
                          {course.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Formateur (optionnel)</Label>
                  <Select value={formData.trainer_id || "self"} onValueChange={(v) => handleInputChange('trainer_id', v === "self" ? "" : v)}>
                    <SelectTrigger data-testid="select-trainer">
                      <SelectValue placeholder="Assigner un formateur" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="self">Moi-même</SelectItem>
                      {trainers.map(trainer => (
                        <SelectItem key={trainer.id} value={trainer.id.toString()}>
                          {trainer.first_name} {trainer.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Session Details */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Video className="w-5 h-5 text-emerald-600" />
                Détails de la Session
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">Titre de la session *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Ex: React Hooks Avancés - Questions/Réponses"
                  data-testid="input-title"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-2 block">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Décrivez le contenu et les objectifs de la session..."
                  rows={3}
                  data-testid="input-description"
                />
              </div>
            </CardContent>
          </Card>

          {/* Schedule */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-600" />
                Planification
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Date *</Label>
                  <Input
                    type="date"
                    min={minDate}
                    value={formData.scheduled_date}
                    onChange={(e) => handleInputChange('scheduled_date', e.target.value)}
                    data-testid="input-date"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Heure de début *</Label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => handleInputChange('start_time', e.target.value)}
                    data-testid="input-start-time"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Heure de fin</Label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => handleInputChange('end_time', e.target.value)}
                    data-testid="input-end-time"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Durée (min)</Label>
                  <div className="flex items-center gap-2 h-10 px-3 border border-slate-200 dark:border-slate-700 rounded-md bg-slate-50 dark:bg-slate-800">
                    <Clock className="w-4 h-4 text-slate-500" />
                    <span className="text-sm font-medium">{formData.duration_minutes} min</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Meeting Link */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link className="w-5 h-5 text-emerald-600" />
                Lien de Réunion
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  URL de la session (Zoom / Google Meet / Teams)
                </Label>
                <Input
                  type="url"
                  value={formData.live_url}
                  onChange={(e) => handleInputChange('live_url', e.target.value)}
                  placeholder="https://meet.google.com/xxx-xxxx-xxx"
                  data-testid="input-live-url"
                />
                <p className="text-xs text-slate-500 mt-2">
                  Collez le lien de votre visioconférence. Les étudiants verront ce lien avant la session.
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleInputChange('live_url', 'https://meet.google.com/')}
                >
                  <img src="https://upload.wikimedia.org/wikipedia/commons/9/9b/Google_Meet_icon_%282020%29.svg" className="w-4 h-4 mr-2" alt="Meet" />
                  Google Meet
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleInputChange('live_url', 'https://zoom.us/j/')}
                >
                  <img src="https://upload.wikimedia.org/wikipedia/commons/7/7b/Zoom_Communications_Logo.svg" className="w-4 h-4 mr-2" alt="Zoom" />
                  Zoom
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleInputChange('live_url', 'https://teams.microsoft.com/l/meetup-join/')}
                >
                  <img src="https://upload.wikimedia.org/wikipedia/commons/c/c9/Microsoft_Office_Teams_%282018%E2%80%93present%29.svg" className="w-4 h-4 mr-2" alt="Teams" />
                  Microsoft Teams
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button 
              type="button" 
              variant="outline"
              onClick={() => navigate('/dashboard')}
            >
              Annuler
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="submit-live"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Planification en cours...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  Planifier la session
                </span>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default ScheduleLivePage;
